import $ from "jquery";
import { debounce, hasScrollReachedBottom, isScrollable, isHidden } from "@/helpers/utils";
const CONFIG = {
    COOKIES: {
        RADIO_SELECTION: "insight-us-cookie-set-radio",
        DO_NOT_ACCEPT: "insight-us-cookie-donotaccept",
        LEGAL_DISCLAIMER: "insight-us-legal-disclaimer",
        USER_CHANNEL: "insight-user-selected-channel",
        COUNTRY_PAGE: "insight-aus-country-page"
    },
    URLS: {
        LEGAL_DISCLAIMER: "/CountryLandingPage/GetLegalDisclaimer",
        T65_HUB_VIEW_COUNT: "/T65HubResults/AddViewedPromoBlock",
        T64_HUB_VIEW_COUNT: "/T64HubLandingPage/AddViewedPromoBlock",
        WIDE_HUB_VIEW_COUNT: "/WideLatestHubPromoBlock/AddViewedPromoBlock",
        PERSPECTIVES_VIEW_COUNT: "/PerspectivesLanding/AddViewedPromoBlock"
    },
    SELECTORS: {
        CONTAINER: ".js-legal-disclaimer-container",
        AUDIENCE_CATEGORIES_SELECT: "#AudienceCategories",
        LEGAL_DISCLAIMER_ACCEPT_BUTTON: ".js-legal-disclaimer_button-accept",
        LEGAL_DISCLAIMER_CLOSE_BUTTON: ".js-legal-disclaimer_close"
    }
};
const Utils = {
    getDataAttribute: (element, attribute, defaultValue = '') => {
        try {
            const $el = typeof element === 'string' ? $(element) : $(element);
            return $el.attr(`data-${attribute}`) || defaultValue;
        }
        catch (error) {
            console.warn(`Error getting data attribute ${attribute}:`, error);
            return defaultValue;
        }
    },
    getElementValue: (selector, defaultValue = '') => {
        try {
            const element = $(selector);
            if (element.length === 0)
                return defaultValue;
            const value = element.val();
            return (typeof value === 'string') ? value : defaultValue;
        }
        catch (error) {
            console.warn(`Error getting element value for ${selector}:`, error);
            return defaultValue;
        }
    },
    parseIntSafe: (value, defaultValue = 0) => {
        const parsed = parseInt(value.toString(), 10);
        return isNaN(parsed) ? defaultValue : parsed;
    },
    elementExists: (selector) => {
        try {
            return $(selector).length > 0;
        }
        catch (error) {
            return false;
        }
    }
};
const CookieManager = {
    set: (name, value, options = {}) => {
        try {
            const defaultOptions = {
                path: "/",
                expires: 3,
                duration: "months"
            };
            const opts = Object.assign(Object.assign({}, defaultOptions), options);
            let expires = "";
            if (opts.expires !== 0) {
                const date = new Date();
                switch (opts.duration) {
                    case "seconds":
                        date.setTime(date.getTime() + opts.expires * 1000);
                        break;
                    case "years":
                        date.setFullYear(date.getFullYear() + opts.expires);
                        break;
                    case "months":
                        date.setMonth(date.getMonth() + opts.expires);
                        break;
                }
                expires = `; expires=${date.toUTCString()}`;
            }
            document.cookie = `${name}=${encodeURIComponent(value || "")}${expires}; path=${opts.path}`;
            return true;
        }
        catch (error) {
            console.error(`Error setting cookie ${name}:`, error);
            return false;
        }
    },
    get: (name) => {
        try {
            if (!document.cookie)
                return null;
            const cookies = document.cookie.split(";");
            for (let cookie of cookies) {
                const trimmedCookie = cookie.trim();
                if (trimmedCookie.startsWith(`${name}=`)) {
                    return decodeURIComponent(trimmedCookie.substring(name.length + 1));
                }
            }
            return null;
        }
        catch (error) {
            console.error(`Error getting cookie ${name}:`, error);
            return null;
        }
    },
    remove: (name) => {
        try {
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
            return true;
        }
        catch (error) {
            console.error(`Error removing cookie ${name}:`, error);
            return false;
        }
    }
};
const ChannelManager = {
    getChannelId: (countryChannel) => {
        if (!countryChannel)
            return 0;
        try {
            const legalCookieCountry = CookieManager.get(CONFIG.COOKIES.COUNTRY_PAGE) || "";
            const channels = countryChannel.split("|");
            for (let channel of channels) {
                const [country, channelId] = channel.split(",");
                if (country === legalCookieCountry || country === "" || legalCookieCountry === "") {
                    return Utils.parseIntSafe(channelId);
                }
            }
            return 0;
        }
        catch (error) {
            console.error("Error getting channel ID:", error);
            return 0;
        }
    },
    validateAndSaveChannelCookie: (channelId) => {
        if (!channelId)
            return false;
        try {
            const existingChannels = CookieManager.get(CONFIG.COOKIES.USER_CHANNEL) || "";
            let newSelectedChannels = "";
            let channelExists = false;
            if (existingChannels) {
                const channels = existingChannels.split("|");
                const [newCountry] = channelId.split(",");
                for (let i = 0; i < channels.length; i++) {
                    const [existingCountry] = channels[i].split(",");
                    if (existingCountry === newCountry) {
                        channelExists = true;
                        newSelectedChannels = existingChannels.replace(channels[i], channelId);
                        break;
                    }
                }
                if (!channelExists) {
                    newSelectedChannels = `${existingChannels}|${channelId}`;
                }
            }
            else {
                newSelectedChannels = channelId;
            }
            return CookieManager.set(CONFIG.COOKIES.USER_CHANNEL, newSelectedChannels);
        }
        catch (error) {
            console.error("Error validating and saving channel cookie:", error);
            return false;
        }
    },
    validateLegalChannel: (channelId) => {
        try {
            const channelIdFromSelect = $(`${CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT} option[data-channelguid$="${ChannelManager.getChannelId(channelId)}"]`).attr("data-channelid");
            const legalCookieValue = CookieManager.get(CONFIG.COOKIES.LEGAL_DISCLAIMER);
            if (!channelIdFromSelect || !legalCookieValue)
                return false;
            const existingCookies = legalCookieValue.split("|");
            return existingCookies.includes(channelIdFromSelect);
        }
        catch (error) {
            console.error("Error validating legal channel:", error);
            return false;
        }
    }
};
const ViewCountManager = {
    updateViewCount: (contentId, customUrl) => {
        if (!contentId)
            return;
        try {
            const url = customUrl || ViewCountManager.getViewCountUrl();
            $.ajax({
                type: "POST",
                url: url,
                data: { contentId: contentId },
                success: () => console.log(`View count updated for content ${contentId}`),
                error: (error) => console.error("Error updating view count:", error)
            });
        }
        catch (error) {
            console.error("Error in updateViewCount:", error);
        }
    },
    getViewCountUrl: () => {
        if (Utils.elementExists(".js-t65-hub-blocks")) {
            return CONFIG.URLS.T65_HUB_VIEW_COUNT;
        }
        else if (Utils.elementExists(".js-t64-hub-blocks")) {
            return CONFIG.URLS.T64_HUB_VIEW_COUNT;
        }
        else if (Utils.elementExists(".js-hub-promo-blocks")) {
            return CONFIG.URLS.WIDE_HUB_VIEW_COUNT;
        }
        return CONFIG.URLS.PERSPECTIVES_VIEW_COUNT;
    }
};
let isInitialized = false;
let hasSelectedChannel = false;
let defaultCountryValue = '';
export const legalDisclaimer = {
    init: function (legalDisclaimerElement) {
        this.legalDisclaimerElement = legalDisclaimerElement;
        if (isInitialized) {
            console.log('⚠️ Legal disclaimer already initialized, skipping');
            return;
        }
        console.log("🔍 Legal Disclaimer: Starting comprehensive initialization");
        isInitialized = true;
        try {
            this.cleanup();
            this.initDisclaimerLogic();
            this.setupChannelSelectionHandlers();
            this.setupPageShowHandler();
            this.setupInteractionHandlers();
            this.setCountryCookie();
            console.log("✅ Legal disclaimer system fully initialized");
        }
        catch (error) {
            console.error("❌ Legal disclaimer initialization failed:", error);
            isInitialized = false;
        }
    },
    cleanup: function () {
        console.log("🧹 Cleaning up existing legal disclaimer handlers");
        $(document).off('.legalDisclaimer');
        $(CONFIG.SELECTORS.LEGAL_DISCLAIMER_CLOSE_BUTTON + ', .js-legal-disclaimer_button-decline, ' + CONFIG.SELECTORS.LEGAL_DISCLAIMER_ACCEPT_BUTTON).off('.legalDisclaimer');
        hasSelectedChannel = false;
    },
    initDisclaimerLogic: function () {
        console.log('🔧 Initializing disclaimer display logic');
        try {
            const channelExists = Utils.getDataAttribute("#header", "channelexists");
            const legalChannelCookieId = CookieManager.get(CONFIG.COOKIES.USER_CHANNEL);
            const isPortalPage = Utils.getDataAttribute("#header", "isportalpage");
            console.log('📊 Legal disclaimer context:', { channelExists, legalChannelCookieId, isPortalPage });
            if (channelExists === "False" && isPortalPage !== "True") {
                console.log('✅ No channels exist - no legal disclaimer needed');
                return;
            }
            if (!document.querySelector(CONFIG.SELECTORS.CONTAINER)) {
                console.log('ℹ️ No legal disclaimer content found in DOM');
                return;
            }
            let legalId = "";
            if (legalChannelCookieId && (channelExists === "True" || isPortalPage)) {
                legalId = Utils.getDataAttribute(`${CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT} option[data-channelguid$="${ChannelManager.getChannelId(legalChannelCookieId)}"]`, "channelid");
            }
            console.log('📊 Legal ID determined:', legalId);
            const shouldShow = this.shouldShowDisclaimer(legalId, channelExists);
            console.log('🔍 Should show disclaimer:', shouldShow, 'for legalId:', legalId, 'channelExists:', channelExists);
            if (!shouldShow) {
                console.log('✅ Legal disclaimer not required');
                return;
            }
            const hasDeclinedCookies = CookieManager.get(CONFIG.COOKIES.DO_NOT_ACCEPT);
            console.log('📊 Cookie status:', { hasDeclinedCookies });
            if (!hasDeclinedCookies && shouldShow) {
                console.log('🔧 Opening legal disclaimer modal...');
                this.open();
            }
            else {
                console.log('✅ Legal disclaimer already handled by user');
            }
        }
        catch (error) {
            console.error("❌ Error in disclaimer logic:", error);
        }
    },
    shouldShowDisclaimer: function (legalId, channelExists) {
        const legalCookieValue = CookieManager.get(CONFIG.COOKIES.LEGAL_DISCLAIMER);
        console.log('🔍 shouldShowDisclaimer debug:', {
            legalId,
            channelExists,
            legalCookieValue
        });
        if (!legalCookieValue) {
            console.log('🔍 No legal cookie found, showing disclaimer');
            return true;
        }
        if (!legalId && channelExists === "True") {
            console.log('🔍 No legal ID but channels exist, showing disclaimer');
            return true;
        }
        if (legalId && legalCookieValue) {
            const existingCookies = legalCookieValue.split("|");
            const legalIdList = legalId.split("|");
            const alreadyAccepted = legalIdList.some(id => existingCookies.includes(id));
            console.log('🔍 Checking if legal ID already accepted:', {
                legalIdList,
                existingCookies,
                alreadyAccepted
            });
            return !alreadyAccepted;
        }
        console.log('🔍 Default case: showing disclaimer');
        return true;
    },
    setupScrollForContainer: function (containerElement) {
        var _a;
        if (!containerElement) {
            console.warn("⚠️ No container element provided");
            return;
        }
        const scrollWrapperElement = containerElement.querySelector('.js-legal-disclaimer-scroll-container');
        if (!scrollWrapperElement) {
            console.log("ℹ️ No scroll container found in element, enabling accept button immediately");
            const acceptButton = containerElement.querySelector(CONFIG.SELECTORS.LEGAL_DISCLAIMER_ACCEPT_BUTTON);
            if (acceptButton) {
                acceptButton.removeAttribute("disabled");
            }
            return;
        }
        console.log("🔍 Setting up scroll tracking for visible container");
        const acceptButton = (_a = scrollWrapperElement.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector(CONFIG.SELECTORS.LEGAL_DISCLAIMER_ACCEPT_BUTTON);
        const scrollCallback = (callback) => {
            const hasUserScrolledToBottom = hasScrollReachedBottom(scrollWrapperElement, 20);
            console.log("🔍 Has scrolled to bottom:", hasUserScrolledToBottom);
            callback === null || callback === void 0 ? void 0 : callback(hasUserScrolledToBottom);
            if (hasUserScrolledToBottom) {
                console.log("✅ Enabling accept button");
                acceptButton === null || acceptButton === void 0 ? void 0 : acceptButton.removeAttribute("disabled");
            }
            else {
                console.log("❌ Disabling accept button");
                acceptButton === null || acceptButton === void 0 ? void 0 : acceptButton.setAttribute("disabled", "true");
            }
        };
        const scrollableCheck = isScrollable(scrollWrapperElement);
        if (scrollableCheck.vertical) {
            console.log("✅ Container is vertically scrollable");
            scrollWrapperElement.scrollTop = 0;
            acceptButton === null || acceptButton === void 0 ? void 0 : acceptButton.setAttribute("disabled", "true");
            let hasUserScrolledToBottom = false;
            const debounceFunction = debounce(50, () => {
                if (!hasUserScrolledToBottom) {
                    scrollCallback((scrollResult) => {
                        hasUserScrolledToBottom = scrollResult;
                    });
                }
            });
            scrollWrapperElement.removeEventListener('scroll', debounceFunction);
            scrollWrapperElement.addEventListener('scroll', debounceFunction);
        }
        else {
            console.log("ℹ️ Container is not scrollable, enabling accept button immediately");
            acceptButton === null || acceptButton === void 0 ? void 0 : acceptButton.removeAttribute("disabled");
        }
    },
    handleChannelDisplay: function (channelId, channelGuid) {
        console.log("🔄 Handling channel display for:", channelId);
        const regionSelector = this.legalDisclaimerElement.querySelector('.js-legal-disclaimer-region-selector');
        const regionSelectorContainer = this.legalDisclaimerElement.querySelector('.js-legal-disclaimer-region-selector-container');
        const contentDiv = document.getElementById(channelId);
        if (regionSelectorContainer) {
            regionSelectorContainer.innerHTML = '';
        }
        if (contentDiv) {
            contentDiv.style.display = 'block';
            if (regionSelector && regionSelectorContainer) {
                const regionSelectorClone = regionSelector.cloneNode(true);
                regionSelectorClone.removeAttribute('hidden');
                regionSelectorContainer.appendChild(regionSelectorClone);
            }
            hasSelectedChannel = true;
            this.setupScrollForContainer(contentDiv);
            this.setupCountrySelectionHandler();
            this.setupCloseButtonHandler(contentDiv);
            console.log("✅ Channel display handlers configured");
        }
    },
    handleSingleChannelAutoDisplay: function () {
        console.log("🔍 Checking for single channel auto-display scenario");
        const visibleChannelContent = this.legalDisclaimerElement.querySelector('div[data-channelguid]');
        if (!visibleChannelContent) {
            console.warn("⚠️ No channel content found");
            return;
        }
        const channelId = visibleChannelContent.id;
        const channelGuid = visibleChannelContent.getAttribute('data-channelguid') || '';
        console.log("✅ Found auto-displayed channel, triggering display logic");
        this.handleChannelDisplay(channelId, channelGuid);
        hasSelectedChannel = true;
    },
    setupChannelSelectionHandlers: function () {
        const channelSelector = this.legalDisclaimerElement.querySelector(CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT);
        if (isHidden(channelSelector)) {
            console.warn("⚠️ Channel selector hidden - checking for single channel auto-display");
            this.handleSingleChannelAutoDisplay();
            return;
        }
        const existingListener = channelSelector._channelChangeListener;
        if (existingListener) {
            channelSelector.removeEventListener('change', existingListener);
        }
        const changeListener = (event) => {
            console.log("🔄 Channel selection changed");
            try {
                const target = event.target;
                const selectedOption = target.options[target.selectedIndex];
                const channelGuid = selectedOption.getAttribute('data-channelguid') || '';
                const channelId = selectedOption.value || '';
                const allAcceptButtons = document.querySelectorAll(CONFIG.SELECTORS.LEGAL_DISCLAIMER_ACCEPT_BUTTON);
                allAcceptButtons.forEach((button) => {
                    button.setAttribute("disabled", "true");
                });
                const options = channelSelector.querySelectorAll("option");
                options.forEach((option) => {
                    const id = option.value || '';
                    const contentDiv = document.getElementById(id);
                    if (contentDiv) {
                        contentDiv.style.display = 'none';
                    }
                });
                this.handleChannelDisplay(channelId, channelGuid);
            }
            catch (error) {
                console.error("Error in channel selection handler:", error);
            }
        };
        channelSelector._channelChangeListener = changeListener;
        channelSelector.addEventListener('change', changeListener);
    },
    setupCountrySelectionHandler: function () {
        const countrySelector = document.getElementById('CountrySelect');
        if (!countrySelector) {
            console.warn("⚠️ Country selector not found");
            return;
        }
        if (!defaultCountryValue) {
            defaultCountryValue = countrySelector.value;
            console.log("📊 Stored default country value:", defaultCountryValue);
        }
        const existingListener = countrySelector._countryChangeListener;
        if (existingListener) {
            countrySelector.removeEventListener('change', existingListener);
        }
        const changeListener = function (event) {
            console.log("🔄 Country selection changed");
            try {
                const target = event.target;
                const selectedOption = target.options[target.selectedIndex];
                const url = selectedOption.value;
                if (url) {
                    console.log("🔄 Navigating to:", url);
                    window.location.href = url;
                }
                else {
                    console.warn("⚠️ No URL found for selected country");
                }
            }
            catch (error) {
                console.error("Error in country selection handler:", error);
            }
        };
        countrySelector._countryChangeListener = changeListener;
        countrySelector.addEventListener('change', changeListener);
        console.log("✅ Country selection handler configured");
    },
    setupCloseButtonHandler: function (contentDiv) {
        const closeButton = contentDiv.querySelector(CONFIG.SELECTORS.LEGAL_DISCLAIMER_CLOSE_BUTTON);
        if (!closeButton) {
            console.warn("⚠️ Close button not found in panel");
            return;
        }
        const existingListener = closeButton._closeClickListener;
        if (existingListener) {
            closeButton.removeEventListener('click', existingListener);
        }
        const clickListener = (event) => {
            event.preventDefault();
            event.stopPropagation();
            console.log("🔄 Close button clicked");
            const redirect = closeButton.getAttribute('data-redirect') || "/";
            setTimeout(() => {
                console.log("🔄 Redirecting to:", redirect);
                window.location.href = redirect;
            }, 100);
        };
        closeButton._closeClickListener = clickListener;
        closeButton.addEventListener('click', clickListener);
        console.log("✅ Close button handler configured");
    },
    setupPageShowHandler: function () {
        window.addEventListener('pageshow', (event) => {
            if (event.persisted && document.querySelectorAll(CONFIG.SELECTORS.CONTAINER).length > 0) {
                console.log("🔄 Page restored from cache, reinitializing disclaimer");
                if (defaultCountryValue) {
                    const countrySelector = document.getElementById('CountrySelect');
                    if (countrySelector) {
                        countrySelector.value = defaultCountryValue;
                        console.log("📊 Restored country dropdown to default value:", defaultCountryValue);
                    }
                }
                isInitialized = false;
                this.init(this.legalDisclaimerElement);
            }
        });
    },
    setupInteractionHandlers: function () {
        console.log("Setting up user interaction handlers");
        let acceptClicked = false;
        $(document).off('click.legalDisclaimer', `${CONFIG.SELECTORS.LEGAL_DISCLAIMER_CLOSE_BUTTON}, .js-legal-disclaimer_button-decline`)
            .on('click.legalDisclaimer', `${CONFIG.SELECTORS.LEGAL_DISCLAIMER_CLOSE_BUTTON}, .js-legal-disclaimer_button-decline`, (event) => {
            event.preventDefault();
            event.stopPropagation();
            console.log("🔄 Close/decline button activated");
            const redirect = $(event.currentTarget).attr('data-redirect') || $(event.currentTarget).data('redirect') || "/";
            setTimeout(() => {
                console.log("🔄 Redirecting to:", redirect);
                window.location.href = redirect;
            }, 100);
            return false;
        });
        $(document).off('click.legalDisclaimer', CONFIG.SELECTORS.LEGAL_DISCLAIMER_ACCEPT_BUTTON)
            .on('click.legalDisclaimer', CONFIG.SELECTORS.LEGAL_DISCLAIMER_ACCEPT_BUTTON, (event) => {
            event.preventDefault();
            event.stopPropagation();
            console.log("🔄 Accept button activated");
            acceptClicked = true;
            this.handleAccept(event.currentTarget, null, null, null);
            return false;
        });
        this.legalDisclaimerElement.addEventListener('close', () => {
            if (!hasSelectedChannel) {
                console.log("⚠️ No channel selected - preventing modal close");
                setTimeout(() => {
                    if (!this.legalDisclaimerElement.open) {
                        this.legalDisclaimerElement.showModal();
                    }
                }, 0);
                return;
            }
            if (!acceptClicked) {
                console.log("🔄 Dialog closed via ESC/backdrop - treating as decline");
                const declineButton = this.legalDisclaimerElement.querySelector(`.js-legal-disclaimer_button-decline, ${CONFIG.SELECTORS.LEGAL_DISCLAIMER_CLOSE_BUTTON}`);
                const redirect = (declineButton === null || declineButton === void 0 ? void 0 : declineButton.getAttribute('data-redirect')) || "/";
                setTimeout(() => {
                    console.log("🔄 Redirecting to:", redirect);
                    window.location.href = redirect;
                }, 100);
            }
            else {
                acceptClicked = false;
            }
        });
        console.log("✅ User interaction handlers configured successfully");
    },
    handleAccept: function (currentTarget, href, target, contentId) {
        try {
            console.log("🔧 Processing accept button interaction");
            const countryChannel = this.getCountryChannelFromTarget(currentTarget);
            if (countryChannel) {
                ChannelManager.validateAndSaveChannelCookie(countryChannel);
            }
            else {
                if ($(CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT).val()) {
                    const selectedOption = $(`${CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT} option:selected`);
                    const channelGuid = Utils.getDataAttribute(selectedOption[0], "channelguid");
                    if (channelGuid) {
                        ChannelManager.validateAndSaveChannelCookie(channelGuid);
                        console.log("📊 Saved channel from selected option:", channelGuid);
                    }
                }
            }
            if (contentId) {
                ViewCountManager.updateViewCount(contentId);
            }
            this.saveLegalCookie();
            CookieManager.remove(CONFIG.COOKIES.RADIO_SELECTION);
            if (href) {
                window.open(href, target || "_self");
                if (target !== "_self") {
                    window.location.reload();
                }
            }
            else {
                window.location.reload();
            }
        }
        catch (error) {
            console.error("Error handling legal disclaimer acceptance:", error);
            window.location.reload();
        }
    },
    getCountryChannelFromTarget: function (target) {
        if (!target)
            return '';
        const $target = $(target);
        const $closestDiv = $target.closest("div[name$=ld]");
        if ($closestDiv.length) {
            return Utils.getDataAttribute($closestDiv[0], "channelguid");
        }
        const selectValue = $(CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT).val();
        if (selectValue) {
            const selectedOption = $(`${CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT} option[value="${selectValue}"]`);
            if (selectedOption.length > 0) {
                return Utils.getDataAttribute(selectedOption[0], "channelguid");
            }
        }
        console.log("⚠️ No channel selected, proceeding without channel");
        return '';
    },
    saveLegalCookie: function () {
        try {
            const legalChannelCookieId = CookieManager.get(CONFIG.COOKIES.USER_CHANNEL);
            const channelExists = Utils.getDataAttribute("#header", "channelexists");
            console.log("📊 saveLegalCookie - channelExists:", channelExists, "legalChannelCookieId:", legalChannelCookieId);
            let legalChannelId = this.getLegalChannelId(legalChannelCookieId, channelExists);
            let legalPageName = Utils.getDataAttribute("#header", "legal-page");
            if (channelExists === "False" || channelExists === undefined) {
                legalPageName = Utils.getDataAttribute(`${CONFIG.SELECTORS.CONTAINER} div[name$=ld]`, "channelid");
            }
            if (!legalChannelId && channelExists === "True") {
                const selectValue = $(`${CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT}`).val();
                if (selectValue) {
                    legalChannelId = Utils.getDataAttribute(`${CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT} option[value="${selectValue}"]`, "channelid");
                    console.log("📊 Got channel ID from select value:", legalChannelId);
                }
            }
            if (!legalChannelId && legalPageName) {
                legalChannelId = legalPageName;
                console.log("📊 Using legalPageName as fallback:", legalChannelId);
            }
            if (!legalChannelId) {
                const visibleDisclaimer = $(`${CONFIG.SELECTORS.CONTAINER} div[name$=ld]:visible`).first();
                if (visibleDisclaimer.length > 0) {
                    legalChannelId = Utils.getDataAttribute(visibleDisclaimer[0], "channelid");
                    console.log("📊 Using visible disclaimer channel ID:", legalChannelId);
                }
            }
            const finalCookieValue = this.buildLegalCookieValue(legalChannelId, legalPageName);
            if (finalCookieValue) {
                CookieManager.set(CONFIG.COOKIES.LEGAL_DISCLAIMER, finalCookieValue);
                console.log("✅ Legal disclaimer cookie saved:", finalCookieValue);
            }
            else {
                console.warn("⚠️ No legal channel ID found to save");
            }
        }
        catch (error) {
            console.error("Error saving legal acceptance cookie:", error);
        }
    },
    getLegalChannelId: function (legalChannelCookieId) {
        if (!legalChannelCookieId)
            return null;
        const channelId = ChannelManager.getChannelId(legalChannelCookieId);
        if (channelId === 0)
            return null;
        return Utils.getDataAttribute(`${CONFIG.SELECTORS.AUDIENCE_CATEGORIES_SELECT} option[data-channelguid$="${channelId}"]`, "channelid");
    },
    buildLegalCookieValue: function (legalChannelId, legalPageName) {
        const existingCookieValue = CookieManager.get(CONFIG.COOKIES.LEGAL_DISCLAIMER);
        if (!legalChannelId && !legalPageName)
            return null;
        const targetId = legalChannelId || legalPageName;
        let cookieIds = existingCookieValue ? existingCookieValue.split("|") : [];
        if (!cookieIds.includes(targetId)) {
            cookieIds.push(targetId);
        }
        return cookieIds.join("|");
    },
    setCountryCookie: function () {
        const countryId = Utils.getDataAttribute("#header", "country");
        if (countryId) {
            CookieManager.set(CONFIG.COOKIES.COUNTRY_PAGE, countryId);
        }
    },
    open: function (href, target, contentId) {
        try {
            console.log("🔄 Opening legal disclaimer modal");
            if (!this.legalDisclaimerElement) {
                console.error("❌ Legal disclaimer element not found!");
                return;
            }
            console.log("📊 Dialog state before open:", {
                open: this.legalDisclaimerElement.open,
                tagName: this.legalDisclaimerElement.tagName
            });
            if (!this.legalDisclaimerElement.open) {
                this.legalDisclaimerElement.showModal();
                $("html").addClass('u-overflow-hidden');
                console.log("✅ Dialog opened with showModal()");
            }
            if (href)
                $("#href").val(href);
            if (target)
                $("#target").val(target);
            if (contentId)
                $("#contentId").val(contentId);
        }
        catch (error) {
            console.error("Error opening legal disclaimer modal:", error);
        }
    },
    close: function () {
        console.log("🔄 Closing legal disclaimer modal");
        if (this.legalDisclaimerElement && this.legalDisclaimerElement.open) {
            this.legalDisclaimerElement.close();
            $("html").removeClass('u-overflow-hidden');
            console.log("✅ Dialog closed with close()");
        }
    },
    reset: function () {
        console.log("🔄 Resetting legal disclaimer system");
        isInitialized = false;
        this.cleanup();
    },
    checkAndLoadDisclaimer: function (contentId, viewCountUrl, href, target) {
        const requestData = {
            countryPage: Utils.getDataAttribute("#header", "countrypage") || "",
            currentPage: Utils.getDataAttribute("#header", "currentpage") || "",
            siteBlockData: contentId || ""
        };
        if (!requestData.currentPage || !contentId) {
            console.error("❌ Missing required data for legal disclaimer check", requestData);
            if (viewCountUrl) {
                ViewCountManager.updateViewCount(contentId, viewCountUrl);
            }
            if (href) {
                window.open(href, target || "_self");
            }
            return;
        }
        $.ajax({
            type: "GET",
            dataType: "html",
            url: CONFIG.URLS.LEGAL_DISCLAIMER,
            data: requestData,
            success: (data) => {
                const parsed = $.parseHTML(data);
                const ldResult = $(parsed).filter("#legal-disclaimer").val();
                if (ldResult !== undefined) {
                    $("#ajaxLegalDisclaimer").html(data);
                    if (data !== null) {
                        this.open(href || null, target || null, contentId);
                    }
                    else {
                        if (viewCountUrl) {
                            ViewCountManager.updateViewCount(contentId, viewCountUrl);
                        }
                        if (href) {
                            window.open(href, target || "_self");
                        }
                    }
                }
                else {
                    if (viewCountUrl) {
                        ViewCountManager.updateViewCount(contentId, viewCountUrl);
                    }
                    if (href) {
                        window.open(href, target || "_self");
                    }
                }
            },
            error: (xhr, status, error) => {
                console.error("Error checking legal disclaimer:", {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    responseText: xhr.responseText,
                    readyState: xhr.readyState,
                    error: error,
                    url: CONFIG.URLS.LEGAL_DISCLAIMER
                });
                console.log("🔄 AJAX failed, navigating directly to link");
                if (viewCountUrl) {
                    ViewCountManager.updateViewCount(contentId, viewCountUrl);
                }
                if (href) {
                    window.open(href, target || "_self");
                }
            }
        });
    }
};
export const utils = {
    CookieManager,
    ChannelManager,
    ViewCountManager,
    Utils
};
if (typeof window !== 'undefined') {
    window.mainLegalDisclaimer = legalDisclaimer;
    window.legalDisclaimerUtils = utils;
    window.LegalDisclaimerCookieManager = CookieManager;
    window.LegalDisclaimerChannelManager = ChannelManager;
    window.LegalDisclaimerViewCountManager = ViewCountManager;
}
